// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Foreground } from '@front/sharedcomponents/Styles';
import TerceraBaseWindowTemplate from '@front/templates/TerceraBaseWindowTemplate.html';
import TerceraLoginScreenTemplate from '@front/templates/TerceraLoginScreenTemplate.html';
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import { ApplicationLoader } from '@shared/commons/ApplicationLoader';
import { Connection } from '@shared/commons/Connection';
import { RegistrationMode } from '@shared/commons/ConnectionEnums';
import { ConntionInfo } from '@shared/commons/ConnectionInfo';
import { DataCache } from '@shared/commons/DataCache';
import { IsPasteEvent, KeyCode } from '@shared/commons/KeyEventProcessor';
import { LocalStorage } from '@shared/commons/LocalStorage';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { BrandingUtils } from '@shared/commons/UtilsClasses/BrandingUtils';
import { BrowserUtils } from '@shared/utils/BrowserUtils';
import { Resources } from '@shared/localizations/Resources';
import { ConectionResultEnum, ConnectionStates } from '@shared/utils/Connections/connection.types';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { decryptWithDerivedKey, encryptAndDeriveKey } from '@shared/utils/EncryptionUtils';
import { TerceraButtonEvents } from '@shared/utils/Enums/ButtonEnums';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { Themes } from '@shared/utils/ThemesEnum';
import { AuthenticationFactory } from '@shared/utils/ThirdPartyAuthentication/AuthenticationFactory';
import { AuthenticationResponseType } from '@shared/utils/ThirdPartyAuthentication/AuthenticationResponseType';
import { ThirdPartyAuthenticationService, getService } from '@shared/utils/ThirdPartyAuthentication/IAuthenticationService';
import { AdditionalVerificationScreen } from './AdditionalVerificationScreen';
import { NewNetTraderMode } from './NewNetTraderMode';
import { TerceraChangePasswordScreen } from './TerceraChangePasswordScreen';
import { TerceraMessageBox } from './TerceraMessageBox';
import { TerceraWindowBase } from './TerceraWindowBase';
import { TerceraRecoverScreen } from '../screen/TerceraRecoverScreen';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
export class TerceraLoginScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.wasPaste = false;
        this.forceCloseOnLogout = false;
        this.onSavePasswordCheckedChanged = (event) => {
            void this.set('savePasswordChecked', event.currentTarget.checked);
        };
    }
    getType() {
        return ScreensNames.TerceraLoginScreen;
    }
    oninit() {
        super.oninit();
        void this.set('allowPasswordRecovery', !Resources.isHidden('screen.LoginScreen.PasswordRecovery'));
        void this.set('createDemoAccountAllowed', !Resources.isHidden('screen.LoginScreen.CreateDemoAccount'));
        this.observe('connectionInfo', this.onConnectionInfoChanged);
        this.observe('selectedConnectionInfoMenuItem', this.onSelectedConnectionInfoMenuItemChanged);
        this.observe('needLogin', this.PlaceholderLoginChanged);
        this.observe('needPassword', this.PlaceholderPasswordChanged);
        this.observe('needCustomerNo', this.PlaceholderCustomerNoChanged);
        this.observe('inputMode', this.onInputModeChanged);
        this.observe('allowPasswordRecovery inputMode IsCorrectForgotLink', () => {
            void this.set('forgotPasswordVisible', this.get('allowPasswordRecovery') && this.get('inputMode') && this.get('IsCorrectForgotLink')); // updating recover link visibility
        });
        this.on('recoverClick', this.onRecoverClicked);
        this.on('exploreClick', this.onExploreClicked);
        this.on('onThirdPartyLoginBtnClick', this.onThirdPartyLoginBtnClicked);
        this.on('onThirdPartyLoginLinkClick', this.onThirdPartyLoginLinkClicked);
        this.on('onThirdPartyCetificateInfoLinkClick', this.onThirdPartyCetificateInfoLinkClicked);
        this.observe('AllowCustomerNo', this.onAllowCustomerNoChanged);
        this.observe('connectionParams.login', this.onConnectionParamsLoginChanged);
        this.observe('connectionParams.pass', this.onConnectionParamsPassChanged);
        this.observe('connectionParams.customerNo', this.onConnectionParamsCustomerNoChanged);
        this.observe('passwordTextBoxFocused', this.onPasswordFocused);
        this.observe('isDisconnect isOpenErrorMessageScreen', () => {
            this.set('isLoginDisabled', !this.get('isDisconnect') || this.get('isOpenErrorMessageScreen'));
        });
        this.loadCredentials();
        const findSelectedLangItem = function () {
            const langItems = this.get('langItems');
            const cLang = ResourcesManager.GetCurrentAppLang();
            for (let i = 0; i < langItems.length; i++) {
                if (langItems[i].value === cLang) {
                    this.set('selectedlangItem', langItems[i]);
                    break;
                }
            }
        }.bind(this);
        this.observe('visible', (visible) => {
            if (!visible) {
                return;
            }
            removeHtmlAttribute('data-theme');
            if (ApplicationInfo.login_language_selector) {
                findSelectedLangItem();
            }
            this.localize();
        });
        void this.set('login_language_selector', ApplicationInfo.login_language_selector);
        if (ApplicationInfo.login_language_selector) {
            void this.set('height', this.get('height'));
            const languageItems = Resources.getLanguagesItems();
            const languageComboboxItems = [];
            for (let i = 0; i < languageItems.length; i++) {
                const languageItem = languageItems[i];
                languageComboboxItems.push({
                    type: NewContextMenuItemType.Default,
                    text: languageItem.text,
                    value: languageItem.value
                });
            }
            void this.set('langItems', languageComboboxItems);
            findSelectedLangItem();
            this.observe('selectedlangItem', this.onSelectedlangItemChanged);
        }
        this.localize();
    }
    oncomplete() {
        this.connectionStateChange = this.connectionStateChange.bind(this);
        Connection === null || Connection === void 0 ? void 0 : Connection.subscribeOnConnectStateChange(this.connectionStateChange);
        const netTrForm = this.get('parentContainerControl');
        netTrForm.onNetTraderModeChange.Subscribe(function (newState) {
            if (newState === NewNetTraderMode.WorkMode) {
                this.set({ textErrorLabel: ' ' });
            }
        }, this);
        this.Controls.loginBtn.on(TerceraButtonEvents.onClick, this.startLogin.bind(this));
        // this.Controls.closeLoginScr.on(TerceraButtonEvents.onClick, this.closeLoginScrClick.bind(this))
        this.Controls.tbLogin.on('keyDown', this.onLoginKeyDown.bind(this));
        this.Controls.tbLoginCustomerNo.on('keyDown', this.onLoginKeyDown.bind(this));
        this.Controls.tbPwd.on('keyDown', this.onPasswordKeyDown.bind(this));
        this.Controls.tbLogin.getInputElement().addEventListener('input', this.trimStringIfWasPaste.bind(this));
        this.Controls.tbPwd.getInputElement().addEventListener('input', this.trimStringIfWasPaste.bind(this));
        // TODO. Ugly. Refactor.
        void this.set('connectionInfo', ConntionInfo.GetConnectionInfoArray());
        void this.set({ inputMode: ApplicationInfo.LoginScreenType === 0 });
    }
    get ChangePasswordScreen() {
        return MainWindowManager.ChangePasswordScreen;
    }
    getLoginScreenLanguage() {
        if (!ApplicationInfo.login_language_selector) {
            return '';
        }
        const langItem = this.get('selectedlangItem');
        return langItem ? langItem.value : '';
    }
    onInputModeChanged(inputMode) {
        //     // if (!inputMode)
        //     //     this.set({ height: 156, style_addition_body: 'login-screen-additional-Token' });
        //     // else
        //     //     this.set({ height: 440, style_addition_body: 'login-screen-additional-Full' });
    }
    onAllowCustomerNoChanged(isAllow) {
        void this.set({ customerNoIndent: isAllow ? 47 : 0 });
    }
    onSelectedlangItemChanged(n, o) {
        if (n && n === o) {
            return;
        }
        Resources.setLocale(n.value);
    }
    onConnectionInfoChanged(connectionInfo) {
        if (!(connectionInfo === null || connectionInfo === void 0 ? void 0 : connectionInfo.length)) {
            void this.set('connectionInfoMenuItems', []);
        }
        const urlConnectionName = BrowserUtils.getQueryParamFromUrl(location.href, 'connectionName');
        const lastConnectionName = urlConnectionName || LocalStorage.getLastConnectionName() || '';
        let lastConnection = null;
        const menuItems = [];
        const len = connectionInfo.length;
        for (let i = 0; i < len; i++) {
            const connInfo = connectionInfo[i];
            const name = connInfo.Name;
            const newItem = {
                text: name,
                value: connInfo
            };
            if (lastConnectionName === name) {
                lastConnection = newItem;
            }
            menuItems.push(newItem);
        }
        void this.set('connectionInfoMenuItems', menuItems);
        if (isNullOrUndefined(lastConnection) && menuItems.length > 0) {
            lastConnection = menuItems[0];
        }
        if (lastConnection) {
            void this.set('selectedConnectionInfoMenuItem', lastConnection);
            void this.setCredentialsByConnName(lastConnectionName);
        }
    }
    onConnectionSelected(selectedConnectionMenuItem) {
        void this.set('selectedConnectionInfoMenuItem', selectedConnectionMenuItem);
    }
    onSelectedConnectionInfoMenuItemChanged(selectedMenuItem) {
        return __awaiter(this, void 0, void 0, function* () {
            if (selectedMenuItem) {
                const selectedConnectionData = selectedMenuItem.value;
                ConntionInfo.SetActiveConnectionInfo(selectedConnectionData);
                const serverURL = selectedConnectionData.ServerAdress;
                const res = yield this.onConnectionParamsCustomerIpURL(selectedConnectionData.ipURL);
                LocalStorage.setLastConnectionName(selectedConnectionData.Name);
                void this.set('connectionParams.ipURL', res);
                const isSSL = selectedConnectionData.isSSL;
                const brKey = selectedConnectionData.brandingKey;
                const forgotLink = selectedConnectionData.forgotURL;
                const registrationLinks = selectedConnectionData.registrationLinks || [];
                this.migrateOldServerRegistrationLinks(registrationLinks); // #126100
                yield this.set('connectionParams.connectionName', selectedConnectionData.Name);
                void this.set('connectionParams.ServerAdress', serverURL);
                void this.set('connectionParams.isSSL', isSSL);
                void this.set('connectionParams.forgotURL', forgotLink);
                void this.set('connectionParams.registrationLinks', registrationLinks);
                void this.set('connectionParams.brandingKey', brKey);
                void this.set('loginPromptText', selectedConnectionData.BrandingLoginPromptText);
                void this.set({
                    IsCorrectForgotLink: forgotLink !== '-1',
                    ICFRPL: registrationLinks.length > 0, // Check if at least one registration link exists
                    AllowCustomerNo: selectedConnectionData.AllowCustomerNo,
                    needCustomerNo: false,
                    placeholderCustomerNo: Resources.getResource('screen.LoginScreen.CustomerNo')
                });
                const isAllowExploreTheApp = !!selectedConnectionData.exploreTheAppUser;
                void this.set('allowExplore', isAllowExploreTheApp);
                void this.set('connectionParams.exploreTheAppUser', selectedConnectionData.exploreTheAppUser);
                void this.set('connectionParams.exploreTheAppPassword', selectedConnectionData.exploreTheAppPassword);
                void this.set('connectionParams.exploreTheAppLink', selectedConnectionData.exploreTheAppLink);
                void this.set('connectionParams.allowPreviewPassword', selectedConnectionData.allowPasswordPreview);
                this.initializeThirdPartyProvider(selectedConnectionData);
                void this.setCredentialsByConnName(selectedConnectionData.Name);
                this.setChatLinkIfNeed(); // #113542
                this.setTopForSomeControls();
                yield this.SetWebTerminalSettings(selectedConnectionData, brKey);
                if (!ApplicationInfo.isProcessedFirstLogin) {
                    if (this.processThirdPartyLogin() === false) {
                        this.processExternalResourceLogin();
                    }
                    ApplicationInfo.isProcessedFirstLogin = true;
                    this.processExploreTheAppIfNeeded(isAllowExploreTheApp);
                }
            }
        });
    }
    SetWebTerminalSettings(selectedConnectionData, brandingKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!isValidString(brandingKey))
                return;
            // settings from 'Web terminal' tab of BackOffice's: Configuration -> Branding
            MainWindowManager.MainWindow.setBrandingWebHeader();
            BrandingUtils.SetWebPageTitle();
            BrandingUtils.SetWebFavicon();
            this.SetSplashScreen(selectedConnectionData);
            this.SetWebLoginBanner();
            yield this.SetBrandingLocales(selectedConnectionData.Name);
        });
    }
    SetSplashScreen(connData) {
        if (connData) {
            const splashScreen = BrandingUtils.GetWebSplashScreen();
            this.fire('setSplashImg', splashScreen);
        }
    }
    SetBrandingLocales(conName) {
        return __awaiter(this, void 0, void 0, function* () {
            yield ApplicationLoader(conName);
        });
    }
    SetWebLoginBanner() {
        var _a;
        void this.set({
            pictureUrl: (_a = BrandingUtils.GetWebLoginBanner(Themes.Dark)) !== null && _a !== void 0 ? _a : ''
        });
    }
    onConnectionParamsLoginChanged(newVal) {
        if (this.get('needLogin') && newVal) {
            void this.set('needLogin', false);
        }
    }
    onConnectionParamsPassChanged(newVal) {
        if (this.get('needPassword') && newVal) {
            void this.set('needPassword', false);
        }
        void this.set('savePasswordCheckedFromConnectionSettings', false);
    }
    onConnectionParamsCustomerNoChanged(newVal) {
        if (this.get('needCustomerNo') && newVal) {
            void this.set('needCustomerNo', false);
        }
    }
    onConnectionParamsCustomerIpURL(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                if (!url) {
                    resolve(null);
                    return;
                }
                const xhr = new XMLHttpRequest();
                xhr.open('GET', url, true);
                xhr.onreadystatechange = function () {
                    if (this.readyState !== 4) {
                        return;
                    }
                    if (this.status !== 200) {
                        resolve(null);
                        return;
                    }
                    resolve(this.responseText.trim());
                };
                xhr.send();
            });
        });
    }
    PlaceholderLoginChanged(newVal) {
        if (!newVal) {
            void this.set('placeholderLogin', Resources.getResource('screen.LoginScreen.LoginIDLabel'));
        }
        else {
            void this.set('placeholderLogin', Resources.getResource('screen.LoginScreen.Error.loginIdIsEmpty'));
        }
    }
    PlaceholderPasswordChanged(newVal) {
        if (!newVal) {
            void this.set('placeholderPassword', Resources.getResource('screen.LoginScreen.PasswordLabel'));
        }
        else {
            void this.set('placeholderPassword', Resources.getResource('screen.LoginScreen.Error.passwordIdIsEmpty'));
        }
    }
    PlaceholderCustomerNoChanged(newVal) {
        if (!newVal) {
            void this.set('placeholderCustomerNo', Resources.getResource('screen.LoginScreen.CustomerNo'));
        }
        else {
            void this.set('placeholderCustomerNo', Resources.getResource('screen.LoginScreen.Error.CustomerNoIsEmpty'));
        }
    }
    onLoginKeyDown(e) {
        if (!this.get('inputMode')) {
            return;
        }
        const keyDownEvent = e.original;
        if (IsPasteEvent(keyDownEvent)) {
            this.wasPaste = true;
        }
        else if (keyDownEvent.keyCode === KeyCode.ENTER) {
            if (this.get('savePasswordCheckedFromConnectionSettings')) {
                this.startLogin();
                return;
            }
            // TODO. Make it work. this.Controls.tbPwd.setFocus();
            // So instead of using the method above:
            this.Controls.tbPwd.set('focused', true);
        }
    }
    onPasswordKeyDown(e) {
        if (!this.get('inputMode')) {
            return;
        }
        const keyDownEvent = e.original;
        if (IsPasteEvent(keyDownEvent)) {
            this.wasPaste = true;
        }
        else if (keyDownEvent.keyCode === KeyCode.ENTER) {
            this.startLogin();
        }
    }
    startLogin(context, verificationPassword, isExploreMode) {
        if (this.get('isOpenErrorMessageScreen')) {
            return null;
        }
        ApplicationInfo.isExploreMode = !!isExploreMode;
        ApplicationInfo.exploreTheAppLink = ApplicationInfo.isExploreMode
            ? this.get('connectionParams.exploreTheAppLink')
            : '';
        const noLogin = !this.get('connectionParams.login') && !ApplicationInfo.isExploreMode;
        const noPass = !this.get('connectionParams.pass') && !ApplicationInfo.isExploreMode;
        const noCustomerNo = !this.get('connectionParams.customerNo') && !ApplicationInfo.isExploreMode;
        void this.set({
            needLogin: noLogin,
            needPassword: noPass,
            needCustomerNo: noCustomerNo
        });
        const AllowCustomerNo = this.get('AllowCustomerNo');
        if (noLogin || noPass || (noCustomerNo && AllowCustomerNo)) {
            return;
        }
        if (!AllowCustomerNo) {
            void this.set('connectionParams.customerNo', '');
        }
        const state = Connection.getState();
        switch (state) {
            case ConnectionStates.CONNECTED:
                Connection.disconnect();
                void this.set({ textErrorLabel: 'Disconnect...', isDisconnect: true });
                break;
            case ConnectionStates.DISCONNECTED:
            case ConnectionStates.CONNECTION_LOST:
                this.StartConnect(verificationPassword);
                break;
            case ConnectionStates.CONNECTING:
                break;
            default:
                break;
        }
    }
    StartConnect(verificationPassword, token) {
        const connectData = Object.assign({}, this.get('connectionParams'));
        if (verificationPassword && verificationPassword.getType === undefined) {
            connectData.verificationPassword = verificationPassword;
        }
        else {
            connectData.verificationPassword = '';
        }
        void this.set({ textErrorLabel: Resources.getResource('screen.LoginScreen.Connecting'), isDisconnect: false });
        connectData.ServerAdressesArray = connectData.ServerAdress.split(',');
        for (let i = 0, len = connectData.ServerAdressesArray.length; i < len; i++) {
            if (connectData.ServerAdressesArray[i]) {
                connectData.ServerAdressesArray[i] = connectData.ServerAdressesArray[i].trim();
            }
        }
        connectData.reconnect = false;
        if (ApplicationInfo.BuildVersionData) {
            connectData.build_date_day = ApplicationInfo.BuildVersionData.dateDay;
        }
        if (token) {
            const thirdPartyProvider = DataCache.ThirdPartyProvidersValidator.thirdPartyProvider;
            const loginTokenService = getService(ThirdPartyAuthenticationService.LoginToken, thirdPartyProvider);
            if (!isNullOrUndefined(loginTokenService)) {
                loginTokenService.processConnectionData(connectData, token);
            }
            else if (ApplicationInfo.ExternalResourse !== -1) {
                connectData.login = ''; // Do not use user from local storage https://tp.traderevolution.com/entity/89399
                connectData.TokenID = token;
                connectData.ExternalResourse = ApplicationInfo.ExternalResourse; // ExternalResource.HUB;//null;
            }
        }
        connectData.accessToken = '';
        if (ApplicationInfo.isExploreMode) {
            connectData.login = connectData.exploreTheAppUser;
            connectData.pass = connectData.exploreTheAppPassword;
        }
        Connection.connect(connectData);
    }
    connectionStateChange() {
        const state = Connection.getState();
        let btnText;
        switch (state) {
            case ConnectionStates.CONNECTED:
                btnText = Resources.getResource('screen.LoginScreen.LoginButtonText');
                void this.set({
                    textButton: btnText,
                    textErrorLabel: Resources.getResource('screen.LoginScreen.LoadingUserData')
                });
                void this.handleCredentialsAfterConnect();
                void this.set('connectionParams.pass', '');
                break;
            case ConnectionStates.DISCONNECTED: {
                void this.set('reconnectConnectionParams', null);
                const savedPass = this.get('connectionParams.pass');
                const savePasswordCh = this.get('savePasswordChecked');
                if (!savePasswordCh) {
                    void this.set('connectionParams.pass', '');
                }
                btnText = Resources.getResource('screen.LoginScreen.LoginButtonText');
                void this.set({ textButton: btnText, isDisconnect: true });
                const err = Connection.getLocalizedConnectionErrorString(this.get('connectionParams.login'));
                if (isValidString(err)) {
                    const loginTokenService = getService(ThirdPartyAuthenticationService.LoginToken, DataCache.ThirdPartyProvidersValidator.thirdPartyProvider);
                    if (!isNullOrUndefined(loginTokenService) &&
                        loginTokenService.processLoginRejection(Connection.getErrorCode())) {
                        void this.set('textErrorLabel', '');
                        return;
                    }
                    this.openErrorMessageScreen(err);
                }
                else {
                    void this.set('textErrorLabel', '');
                    this.Controls.tbPwd.set('passwordHided', true);
                }
                void this.set('savePasswordCheckedFromConnectionSettings', savePasswordCh && !err); // to prevent the password preview from being hidden when "Save Password" is checked but the entered password is incorrect
                const lastResp = Connection.lastConnectionAttemptResponse;
                if (lastResp.connectReason === ConectionResultEnum.NeedActivation ||
                    lastResp.connectReason === ConectionResultEnum.AdditionalVerification) {
                    const okCallBack = function (verificPass) {
                        this.startLogin(undefined, verificPass);
                    }.bind(this);
                    const cancelCallBack = function () {
                        this.set('textErrorLabel', '');
                        this.set('isDisconnect', true);
                        this.set('connectionParams.pass', '');
                    }.bind(this);
                    void this.set('connectionParams.pass', savedPass);
                    void this.set('isDisconnect', false);
                    AdditionalVerificationScreen.Show(lastResp, okCallBack, cancelCallBack, this.get('connectionParams'));
                }
                if (lastResp.connectReason === ConectionResultEnum.NeedChangePassword) {
                    this.openErrorMessageScreen(Resources.getResource('screen.login.change_pass'));
                }
                if (lastResp.connectReason === ConectionResultEnum.InvalidBrandingKey) {
                    this.openErrorMessageScreen(Resources.getResource('screen.login.bad_branding_key'));
                }
                break;
            }
            case ConnectionStates.CONNECTION_LOST:
                break;
            case ConnectionStates.CONNECTING:
                break;
            case ConnectionStates.HALFCONNECTED: {
                const lastResp = Connection.lastConnectionAttemptResponse;
                const lastConnectionParams = this.get('connectionParams');
                const cancelCB = () => {
                    Connection.disconnect();
                };
                this.showChangePwdScreen(lastResp.UserID, lastConnectionParams.pass, cancelCB);
                break;
            }
            default:
                btnText = 'Default';
                void this.set({ textButton: btnText, textErrorLabel: '' });
                break;
        }
    }
    showChangePwdScreen(UserID = '', OldPass, cancelCB, isTradingPassword = false) {
        TerceraChangePasswordScreen.show(UserID, OldPass, cancelCB, isTradingPassword);
    }
    openErrorMessageScreen(errorText) {
        void this.set({ textErrorLabel: '', isOpenErrorMessageScreen: true });
        const callBack = () => {
            this.closeErrorMessageScreen();
        };
        TerceraMessageBox.Show(Resources.getResource('screen.LoginScreen.LoginError'), errorText, MessageBoxType.Warning, callBack, callBack, false, true, null, {
            style_addition_header: '',
            okText: Resources.getResource('general.messageBox.ok')
        }, true);
    }
    closeErrorMessageScreen() {
        void super.set('isOpenErrorMessageScreen', false);
    }
    handleCredentialsAfterConnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const connectionParams = this.get('connectionParams');
            if (!this.get('reconnectConnectionParams')) {
                void this.set('reconnectConnectionParams', {
                    login: connectionParams.login,
                    // pass: connectionParams.pass, // commented out due to #123655
                    allowPasswordPreview: connectionParams.allowPreviewPassword,
                    connectionName: connectionParams.connectionName,
                    customerNo: connectionParams.customerNo
                });
            }
            const key = ConnectionStates.CONNECTED + connectionParams.login;
            const encryptedPassword = this.get('savePasswordChecked')
                ? yield encryptAndDeriveKey(connectionParams.pass, key)
                : null;
            LocalStorage.setConnectionSettings(connectionParams.connectionName, connectionParams.login, encryptedPassword, connectionParams.customerNo);
        });
    }
    loadCredentials() {
        LocalStorage.loadConnectionSettings();
    }
    setCredentialsByConnName(name) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!ApplicationInfo.saveLastLogin) {
                LocalStorage.removeConnectionSettings();
                return;
            }
            const [u, p, cN] = LocalStorage.getConnectionSettings(name);
            const allowSavePassword = this.get('selectedConnectionInfoMenuItem.value.allowSavePassword');
            const savePasswordChecked = p != null && allowSavePassword;
            const key = ConnectionStates.CONNECTED + u;
            const decryptedPassword = savePasswordChecked ? yield decryptWithDerivedKey(p, key) : '';
            (_a = this.Controls.tbPwd) === null || _a === void 0 ? void 0 : _a.set('passwordHided', true);
            void this.set({
                'connectionParams.login': u,
                'connectionParams.customerNo': cN,
                'connectionParams.pass': decryptedPassword,
                savePasswordChecked
            }).then(() => {
                void this.set('savePasswordCheckedFromConnectionSettings', decryptedPassword !== '');
            });
        });
    }
    onRecoverClicked() {
        const connectionParams = this.get('connectionParams');
        const forgotURL = connectionParams.forgotURL;
        if (forgotURL === '') {
            const terceraRecoverScreen = new TerceraRecoverScreen();
            void terceraRecoverScreen.set({
                connectionName: connectionParams.connectionName,
                // valueLogin: connectionParams.login, /* Commented out for the reason described in #126257 */
                allowPasswordPreview: connectionParams.allowPreviewPassword
            });
            MainWindowManager.MainWindow.Controls.loginWindowsContainer.addControl(terceraRecoverScreen);
            terceraRecoverScreen.gotFocus();
        }
        else {
            window.open(forgotURL);
        } // #83944
    }
    onExploreClicked() {
        this.startLogin(undefined, undefined, true);
    }
    // #region ThirdPartyProvider
    initializeThirdPartyProvider(selectedConnectionData) {
        const params = {
            type: selectedConnectionData.BrandingExternalAuthenticationType,
            url: selectedConnectionData.BrandingExternalAuthenticationUrl,
            clientId: selectedConnectionData.BrandingExternalAuthenticationWebClientId,
            customUrl: selectedConnectionData.BrandingWebCustomUrl,
            logoutUrl: selectedConnectionData.BrandingExternalAuthenticationLogoutUrl
        };
        const thirdPartyProvider = AuthenticationFactory.create(params);
        DataCache.ThirdPartyProvidersValidator.attachProvider(thirdPartyProvider);
        const loginButtonService = getService(ThirdPartyAuthenticationService.LoginButton, thirdPartyProvider);
        const loginLinkService = getService(ThirdPartyAuthenticationService.LoginLink, thirdPartyProvider);
        const certificateService = getService(ThirdPartyAuthenticationService.CertificateLogin, thirdPartyProvider);
        const loginTokenService = getService(ThirdPartyAuthenticationService.LoginToken, thirdPartyProvider);
        const thirdPartyProviderLoginButtonText = loginButtonService === null || loginButtonService === void 0 ? void 0 : loginButtonService.buttonText;
        const thirdPartyProviderCertificateLinkText = certificateService === null || certificateService === void 0 ? void 0 : certificateService.certificateLinkText;
        void this.set('thirdPartyProviderLoginButtonText', thirdPartyProviderLoginButtonText);
        void this.set('thirdPartyProviderLoginLinkText', loginLinkService === null || loginLinkService === void 0 ? void 0 : loginLinkService.loginLinkText);
        void this.set('thirdPartyProviderCertificateLinkText', thirdPartyProviderCertificateLinkText);
        if (!isNullOrUndefined(loginTokenService)) {
            void this.set({ inputMode: loginTokenService.isInputMode() });
        }
    }
    onThirdPartyLoginBtnClicked() {
        const provider = DataCache.ThirdPartyProvidersValidator.thirdPartyProvider;
        const loginButtonService = getService(ThirdPartyAuthenticationService.LoginButton, provider);
        loginButtonService.processLogin();
    }
    onThirdPartyLoginLinkClicked() {
        const provider = DataCache.ThirdPartyProvidersValidator.thirdPartyProvider;
        const loginLinkService = getService(ThirdPartyAuthenticationService.LoginLink, provider);
        loginLinkService.processLoginLink();
    }
    onThirdPartyCetificateInfoLinkClicked() {
        return __awaiter(this, void 0, void 0, function* () {
            const provider = DataCache.ThirdPartyProvidersValidator.thirdPartyProvider;
            const certificateService = getService(ThirdPartyAuthenticationService.CertificateLogin, provider);
            const login = this.get('connectionParams.login');
            void certificateService.showCertificate(login);
        });
    }
    processThirdPartyLogin() {
        const provider = DataCache.ThirdPartyProvidersValidator.thirdPartyProvider;
        if (isNullOrUndefined(provider)) {
            return false;
        }
        const autoLoginService = getService(ThirdPartyAuthenticationService.AutoLogin, provider);
        if (!isNullOrUndefined(autoLoginService) && autoLoginService.processAutoLogin()) {
            return true;
        }
        const loginTokenService = getService(ThirdPartyAuthenticationService.LoginToken, provider);
        if (!isNullOrUndefined(loginTokenService)) {
            const tokenResponse = loginTokenService.processToken();
            switch (tokenResponse.ResponseType) {
                case AuthenticationResponseType.Success:
                    this.StartConnect(undefined, tokenResponse.Result);
                    break;
                case AuthenticationResponseType.Error:
                    void this.set('textErrorLabel', tokenResponse.Result);
                    break;
            }
        }
        return true;
    }
    processExternalResourceLogin() {
        const parsedURL = BrowserUtils.getUrlVars(location.href);
        const tokenFromURL = Object.prototype.hasOwnProperty.call(parsedURL, 'token') ? parsedURL.token : null;
        const tokenToSend = ApplicationInfo.LoginToken || tokenFromURL;
        if (tokenToSend && ApplicationInfo.ExternalResourse !== -1) {
            void this.set({ inputMode: false });
            void this.onConnectionParamsCustomerIpURL(this.get('selectedConnectionInfoMenuItem').value.ipURL).then((res) => {
                void this.set('connectionParams.ipURL', res);
                this.StartConnect(null, tokenToSend);
            });
            return true;
        }
        return false;
    }
    // #endregion
    setChatLinkIfNeed() {
        // #113542 will return true if chat link was set
        const chatLink = !Resources.isHidden('screen.LoginScreen.ChatLink.visible')
            ? Resources.getResource('screen.LoginScreen.ChatLink.url')
            : null;
        void this.set('connectionParams.chatLink', chatLink);
        return !!chatLink;
    }
    setTopForSomeControls() {
        const data = this.get();
        const cNI = data.customerNoIndent;
        const inputMode = data.inputMode;
        const chatLinkVisible = data.connectionParams ? !!data.connectionParams.chatLink : false;
        const forgotVisible = data.forgotPasswordVisible;
        const exploreVisible = data.allowExplore && inputMode;
        const regLinkVisible = data.createDemoAccountAllowed && data.ICFRPL;
        const loginTBTop = cNI + 172; // login text box top
        const passTBTop = cNI + 219; // password text box top
        const langSelTop = cNI + 266; // language (combobox) selector top
        let forgotLinkTop = 395; // forgot password link(btn) top
        let exploreLinkTop = 395; // explore link(top) top
        let regLinkTop = 397; // demo registration link top
        let chatLinkTop = chatLinkVisible ? 395 : 0; // live chat link top #113542
        const loginBtnTop = regLinkVisible ? 334 : 354; // log in button's top
        const errorTextTop = inputMode ? 413 : 128; // text area of connection status/errors top
        if (chatLinkVisible) {
            if (forgotVisible) {
                forgotLinkTop -= 10;
                chatLinkTop += 5;
                regLinkTop -= 5;
            }
            else {
                regLinkTop -= 10;
            }
        }
        if (regLinkVisible) {
            forgotLinkTop -= 20;
            if (chatLinkVisible) {
                regLinkTop -= 10;
            }
        }
        if (exploreVisible) {
            if (forgotVisible) {
                forgotLinkTop -= 10;
            }
            exploreLinkTop = forgotVisible ? forgotLinkTop + 17 : forgotLinkTop;
            regLinkTop = regLinkVisible ? exploreLinkTop + 17 : regLinkTop;
        }
        void this.set({
            loginTBTop,
            passTBTop,
            languageCBTop: langSelTop,
            recoverLinkTop: forgotLinkTop,
            exploreLinkTop,
            chatLinkTop: chatLinkVisible ? chatLinkTop : 0,
            createDemoAccTop: regLinkTop,
            loginBtnTop,
            errorTextTop
        });
    }
    localize() {
        const tooltipsForDemoRegLinks = [];
        const tooltipKeyForFirstDemoRegLink = this.needStandardTooltipForRegLink
            ? 'screen.LoginScreen.RegistrationLinkLabel.StandardToolTip' /* #126100 */
            : 'screen.LoginScreen.RegistrationLinkLabel.ToolTip';
        tooltipsForDemoRegLinks.push(Resources.getResource(tooltipKeyForFirstDemoRegLink));
        tooltipsForDemoRegLinks.push(Resources.getResource('screen.LoginScreen.SecondRegistrationLink.ToolTip'));
        void this.set({
            textSavePassword: Resources.getResource('screen.LoginScreen.SavePassCheckBox'),
            textRecover: Resources.getResource('screen.LoginScreen.ForgotLinkLabel'),
            textExplore: Resources.getResource('screen.LoginScreen.ExploreLinkLabel'),
            textButton: Resources.getResource('screen.LoginScreen.LoginButtonText'),
            chatLinkText: Resources.getResource('screen.LoginScreen.ChatLink.label'),
            tooltipLogin: Resources.getResource('screen.LoginScreen.LoginIDTextBox.ToolTip'),
            tooltipPassword: Resources.getResource('screen.LoginScreen.PasswordTextBox.ToolTip'),
            tooltipSavePassword: Resources.getResource('screen.LoginScreen.SavePassCheckBox.ToolTip'),
            tooltipRecover: Resources.getResource('screen.LoginScreen.ForgotLinkLabel.ToolTip'),
            tooltipExplore: Resources.getResource('screen.LoginScreen.ExploreLinkLabel.ToolTip'),
            tooltipLoginBtn: Resources.getResource('screen.LoginScreen.LoginButtonTooltip'),
            tooltipsForDemoRegLinks,
            tooltipCustomerNo: Resources.getResource('screen.LoginScreen.CustomerNo'),
            placeholderLogin: Resources.getResource('screen.LoginScreen.LoginIDLabel'),
            placeholderPassword: Resources.getResource('screen.LoginScreen.PasswordLabel'),
            savePassText: Resources.getResource('screen.LoginScreen.SavePassCheckBox'),
            placeholderCustomerNo: Resources.getResource('screen.LoginScreen.CustomerNo'),
            langCBLabel: Resources.getResource('screen.LoginScreen.Settings.LanguageLabel')
        });
    }
    trimStringIfWasPaste(event) {
        const input = event.target;
        const insertFromPaste = this.wasPaste || event.inputType === 'insertFromPaste';
        input.value = insertFromPaste ? input.value.trim() : input.value;
        this.wasPaste = false;
    }
    onPasswordFocused(focused) {
        if (focused && this.get('savePasswordCheckedFromConnectionSettings')) {
            void this.set('connectionParams.pass', '');
            void this.set('savePasswordCheckedFromConnectionSettings', false);
        }
    }
    migrateOldServerRegistrationLinks(registrationLinks) {
        this.needStandardTooltipForRegLink = false;
        const firstLink = registrationLinks[0];
        if (firstLink && isValidURL(firstLink.linkHref) && firstLink.text === 'Registration') {
            firstLink.text = Resources.getResource('reports.SETTINGS_REGISTRATION');
            this.needStandardTooltipForRegLink = true;
        }
    }
    processExploreTheAppIfNeeded(isAllowExploreTheApp) {
        if (isAllowExploreTheApp === false)
            return;
        const allowExploreTheApp = BrowserUtils.getQueryParamFromUrl(location.href, 'exploretheapp');
        if (allowExploreTheApp === 'true' || allowExploreTheApp === '1') {
            this.onExploreClicked();
        }
    }
}
TerceraWindowBase.extendWith(TerceraLoginScreen, {
    template: TerceraBaseWindowTemplate,
    data: () => {
        return {
            movable: false,
            showHeader: false,
            showFooter: false,
            resizable: false,
            canSelect: false,
            alwaysMiddle: true,
            textLoginLabel: '',
            textPasswordLabel: '',
            textSavePassword: '',
            textRecover: '',
            textExplore: '',
            textButton: '', // NOSONAR[typescript:S2068]
            tooltipLogin: '',
            tooltipPassword: '',
            savePassText: '',
            savePassTextColor: Foreground.Hint,
            tooltipSavePassword: '',
            tooltipRecover: '',
            tooltipExplore: '',
            tooltipLoginBtn: '',
            tooltipsForDemoRegLinks: [],
            langCBLabel: '',
            chatLinkText: '',
            loginPromptText: '',
            style_addition_body: 'login-screen-additional',
            style_addition_jsWin: 'login-screen-additional-jsWin',
            placeholderLogin: 'Login',
            placeholderPassword: 'Password', // NOSONAR[typescript:S2068]
            savePasswordChecked: false,
            savePasswordCheckedFromConnectionSettings: false,
            passwordTextBoxFocused: false,
            pictureUrl: null,
            comboItems: [
                { value: 'Lang1', text: 'Lang1' },
                { value: 'Lang2', text: 'Lang2' },
                { value: 'Lang3', text: 'Lang3' }
            ],
            textErrorLabel: '',
            connectionParams: {
                login: '',
                pass: '',
                connectionName: ''
            },
            reconnectConnectionParams: null,
            connectionInfo: [],
            connectionInfoMenuItems: [],
            selectedConnectionInfoMenuItem: null,
            isDisconnect: true,
            closeBtnVisible: false,
            needLogin: false,
            needPassword: false,
            needCustomerNo: false,
            allowPasswordRecovery: true,
            allowExplore: false,
            createDemoAccountAllowed: true,
            AllowCustomerNo: false,
            customerNoIndent: 0,
            loginTBTop: 0,
            passTBTop: 0,
            languageCBTop: 0,
            recoverLinkTop: 0,
            chatLinkTop: 0,
            createDemoAccTop: 0,
            loginBtnTop: 0,
            errorTextTop: 0,
            RegistrationModeEnum: RegistrationMode,
            login_language_selector: false,
            langItems: [],
            inputMode: true,
            IsCorrectForgotLink: false,
            forgotPasswordVisible: false,
            ICFRPL: false, // IsCorrectForRegistrationProcessLink,
            isOpenErrorMessageScreen: false,
            setSplashImg: null,
            isLoginDisabled: false
        };
    },
    partials: { bodyPartial: TerceraLoginScreenTemplate },
    computed: {
        loginEnabled: {
            get: function () {
                return this.get('isDisconnect') && !this.get('isOpenErrorMessageScreen');
            }
        }
    }
});
